import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  decryptPatientRecord,
  encryptPatient,
  Patient,
  PatientRecord,
} from "../models/patient";
import { useDatabase } from "../context/DatabaseContext.tsx";
import { v4 as uuid } from "uuid";
import {
  decryptPatientDataRecord,
  encryptPatientData,
  PatientData,
  PatientDataRecord,
} from "../models/patient-data.ts";
import { useLoginContext } from "../context/LoginContext.tsx";

export const usePatients = () => {
  const db = useDatabase();
  const { user } = useLoginContext();
  const queryClient = useQueryClient();
  return useQuery<Patient[]>({
    queryFn: async () => {
      if (!user) throw new Error("Not logged in");
      const patientCollection = db.readCollection<PatientRecord>("patients");
      const list = await patientCollection.listByIndex("byUser", user.userId);
      return list.map((record) => decryptPatientRecord(record, user));
    },
    queryKey: ["patients"],
    onSuccess: (patients) => {
      patients.forEach((patient) =>
        queryClient.setQueryData(["patients", patient.id], patient)
      );
    },
  });
};

export const usePatient = (patientId: string | undefined) => {
  const db = useDatabase();
  const { user } = useLoginContext();
  return useQuery<Patient | undefined>(
    patientId
      ? {
          queryKey: ["patients", patientId],
          queryFn: async () => {
            if (!user) throw new Error("Not logged in");
            const patientCollection =
              db.readCollection<PatientRecord>("patients");
            const patient = await patientCollection.get(patientId);
            return patient && decryptPatientRecord(patient, user);
          },
        }
      : { enabled: false }
  );
};

export const useCreatePatient = () => {
  const db = useDatabase();
  const { user } = useLoginContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientPartial: Omit<Patient, "id" | "lastImport">) => {
      if (!user) throw new Error("not logged in");
      const id = uuid();
      const patientsCollection = db.readWriteCollection("patients");
      const patient = { ...patientPartial, id, lastImport: null };
      const patientRecord = encryptPatient(patient, user);
      await patientsCollection.set(id, patientRecord);
      return patient;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries("patients");
    },
  });
};

export const useUpdatePatient = () => {
  const db = useDatabase();
  const { user } = useLoginContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patient: Patient) => {
      if (!user) throw new Error("not logged in");
      const patientsCollection = db.readWriteCollection("patients");
      const patientRecord = encryptPatient(patient, user);
      await patientsCollection.set(patient.id, patientRecord);
      return patient;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries("patients");
    },
  });
};

export const useDeletePatient = () => {
  const db = useDatabase();
  const { user } = useLoginContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patient: Patient) => {
      if (!user) throw new Error("not logged in");
      const patientsCollection = db.readWriteCollection("patients");
      await patientsCollection.delete(patient.id);
      return patient;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries("patients");
    },
  });
};

export const useDeleteExport = () => {
  const db = useDatabase();
  const { user } = useLoginContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exportId: string) => {
      if (!user) throw new Error("not logged in");
      const patientData = db.readWriteCollection("patientData");
      await patientData.delete(exportId);
      return patientData;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries("patients");
    },
  });
};

export const useAllPatientData = (patientId: string | undefined) => {
  const db = useDatabase();
  const { user } = useLoginContext();
  return useQuery<PatientData[]>(
    patientId
      ? {
          queryFn: async () => {
            if (!user) throw new Error("Not logged in");
            const patientDataCollection =
              db.readCollection<PatientDataRecord>("patientData");
            const list = await patientDataCollection.listByIndex(
              "byPatient",
              IDBKeyRange.only([user.userId, patientId])
            );
            return list.map((data) => decryptPatientDataRecord(data, user));
          },
          queryKey: ["patients", patientId, "data"],
        }
      : { enabled: false }
  );
};

export const useCreatePatientData = () => {
  const db = useDatabase();
  const { user } = useLoginContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      patientData: patientDataPartial,
      patient,
    }: {
      patientData: Omit<PatientData, "exportId" | "lastImport"> & {
        exportId?: string;
      };
      patient: Patient;
    }) => {
      if (!user) throw new Error("not logged in");
      const exportId = patientDataPartial.exportId || uuid();
      const patientsCollection =
        db.readWriteCollection<PatientDataRecord>("patientData");
      const patientData: PatientData = { ...patientDataPartial, exportId };
      const patientRecord = encryptPatientData(patientData, patient, user);
      await patientsCollection.set(exportId, patientRecord);
      return patientData;
    },
    onSuccess: async (_, { patient }) => {
      await queryClient.invalidateQueries(["patients", patient.id, "data"]);
    },
  });
};
